// Sub-position within the `'select-list'` clause: did the cursor land RIGHT
// AFTER a completed projection element (column / star / alias / function-call
// result / literal)? The grammar tagger reports `select-list` for everything
// from `SELECT |` to `SELECT col1, col2, col3 |`, but those positions demand
// different keyword promotion:
//
//   "SELECT "                          ← expecting projection → no FROM promo
//   "SELECT DISTINCT "                 ← expecting projection → no FROM promo
//   "SELECT TOP 10 "                   ← expecting projection → no FROM promo
//   "SELECT * "                        ← projection done → promote FROM tier 1
//   "SELECT col1 "                     ← projection done → promote FROM tier 1
//   "SELECT col1, col2 "               ← projection done → promote FROM tier 1
//   "SELECT col1 AS alias "            ← projection (aliased) done → promote
//   "SELECT col1, "                    ← comma → next projection expected
//   "SELECT col1 AS "                  ← alias-pending → no FROM promo
//
// Mirrors `isAfterCompletedFromRelation` — pure text scan, no parse-tree, safe
// to run on the resolver hot path. Caller is responsible for first confirming
// `positionKind === 'select-list'`; this helper only inspects the last
// non-whitespace token before the cursor.

/** Keywords that, when they are the last bareword before the cursor's leading
 *  whitespace, mean the user is STILL inside the projection-introducer
 *  preamble or in an alias / window-spec slot — i.e. NOT yet "after a
 *  completed projection element". */
import { isInsideNonSubqueryParen } from './clauseShapeSlot';
import type { ScanOptions } from './slotTextScan';

const PROJECTION_INTRODUCING_KEYWORDS: ReadonlySet<string> = new Set([
  // The clause keyword itself.
  'SELECT',
  // Set-quantifier modifiers.
  'DISTINCT',
  'ALL',
  'UNIQUE',
  // T-SQL row-limit modifiers (precede the projection list).
  'TOP',
  'PERCENT',
  // After AS the user is naming the column alias — not yet complete.
  'AS',
  // CASE expression interior tokens — projection element still under
  // construction.
  'CASE',
  'WHEN',
  'THEN',
  'ELSE',
  // Window-spec slot opens.
  'OVER',
  // Set-op continuation keywords (would change positionKind in practice, but
  // listed defensively so the helper never promotes after them).
  'UNION',
  'INTERSECT',
  'EXCEPT',
]);

/**
 * Returns `true` when the cursor sits immediately after a completed projection
 * element inside a SELECT list.
 *
 *   "SELECT * "                  → true
 *   "SELECT col1 "               → true
 *   "SELECT col1, col2 "         → true
 *   "SELECT COUNT(*) "           → true
 *   "SELECT 'foo' "              → true
 *   "SELECT col1 AS my_alias "   → true (last word is the alias name)
 *   "SELECT "                    → false (last word is SELECT)
 *   "SELECT DISTINCT "           → false
 *   "SELECT TOP 10 "             → false (all-digit bareword = modifier)
 *   "SELECT col1 AS "            → false (last word is AS — alias pending)
 *   "SELECT col1, "              → false (comma — next projection expected)
 *   "SELECT col"                 → false (no trailing whitespace — mid-type)
 *   "SELECT COUNT(id "           → false (inside a call's argument list)
 *   "SELECT EXTRACT(YEAR FROM "  → false (inside a call's argument list)
 */
export function isAfterCompletedSelectProjection(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions = {},
): boolean {
  // Inside an unclosed non-subquery paren the caret is in a call's argument
  // list or a parenthesised expression (`SELECT COUNT(id ▮`,
  // `SELECT EXTRACT(YEAR FROM ▮`) — the projection element itself is still
  // open, so `FROM` / set operators are not legal continuations. A paren that
  // opens a subquery (`SELECT (SELECT a ▮`) is a fresh projection scope and
  // stays eligible.
  if (isInsideNonSubqueryParen(text, offset, statementStart, opts)) return false;
  let i = Math.min(offset, text.length);
  // Skip whitespace immediately before the cursor.
  while (i > statementStart && /\s/.test(text[i - 1] ?? '')) i -= 1;
  // No whitespace before cursor → user is mid-token; defer to the typed-prefix
  // ranker (it already handles column-name completion).
  if (i === offset) return false;
  if (i <= statementStart) return false;

  const prev = text[i - 1] ?? '';

  // Star end — `SELECT * `.
  if (prev === '*') return true;
  // Paren-close — `SELECT COUNT(*) ` / `SELECT (subq) `. EXCEPT a `)` that closes
  // a `DISTINCT ON ( … )` modifier (Postgres): that is not a projection element —
  // a projection item is still required after it (`SELECT DISTINCT ON (k) col …`).
  if (prev === ')') return !closesDistinctOnGroup(text, i - 1, statementStart);
  // Quoted identifier close — `SELECT "col" ` / `[col] ` / backtick-col.
  if (prev === ']' || prev === '"' || prev === '`') return true;
  // String literal close — `SELECT 'foo' `.
  if (prev === "'") return true;

  // Bareword end → walk back to extract the word and inspect it.
  if (/[A-Za-z0-9_]/.test(prev)) {
    let j = i;
    while (j > statementStart && /[A-Za-z0-9_]/.test(text[j - 1] ?? '')) j -= 1;
    const word = text.slice(j, i);
    // All-numeric bareword is ambiguous — could be a literal (`SELECT 1 `,
    // technically a completed projection) or a modifier argument
    // (`SELECT TOP 10 `, projection not yet started). The modifier case is
    // far more common in real authoring; bias toward not over-promoting.
    if (/^\d+$/.test(word)) return false;
    return !PROJECTION_INTRODUCING_KEYWORDS.has(word.toUpperCase());
  }

  // Anything else (comma, dot, semicolon, operator) → still mid-construction.
  return false;
}

/** True when the `)` at `closeIdx` closes a `DISTINCT ON ( … )` modifier group
 *  (Postgres) rather than a projection element. After the modifier the user
 *  still owes a projection item, so the caller must NOT treat it as complete. */
function closesDistinctOnGroup(text: string, closeIdx: number, statementStart: number): boolean {
  // Walk back to the matching open paren.
  let depth = 0;
  let open = -1;
  for (let k = closeIdx; k >= statementStart; k -= 1) {
    const ch = text[k] ?? '';
    if (ch === ')') depth += 1;
    else if (ch === '(') {
      depth -= 1;
      if (depth === 0) {
        open = k;
        break;
      }
    }
  }
  if (open < 0) return false;
  // Inspect the two barewords immediately before the open paren: `DISTINCT ON (`.
  let p = open;
  while (p > statementStart && /\s/.test(text[p - 1] ?? '')) p -= 1;
  const w1 = wordEndingAt(text, p, statementStart);
  if (!w1 || w1.word.toUpperCase() !== 'ON') return false;
  let q = w1.start;
  while (q > statementStart && /\s/.test(text[q - 1] ?? '')) q -= 1;
  const w2 = wordEndingAt(text, q, statementStart);
  return !!w2 && w2.word.toUpperCase() === 'DISTINCT';
}

/** Bareword ending exactly at `end` (no trailing-whitespace skipping), or null
 *  when the char before `end` is not an identifier char. */
function wordEndingAt(
  text: string,
  end: number,
  statementStart: number,
): { readonly word: string; readonly start: number } | null {
  if (end <= statementStart) return null;
  if (!/[A-Za-z0-9_]/.test(text[end - 1] ?? '')) return null;
  let j = end;
  while (j > statementStart && /[A-Za-z0-9_]/.test(text[j - 1] ?? '')) j -= 1;
  return { word: text.slice(j, end), start: j };
}
